/**
 * Progression and the recap — phase 8. Mirrors `src/http/recap.rs`.
 *
 * The two calls live here rather than in a service, because neither is a flow:
 * a recap is one `GET` by id and a career is one `GET` of your own history.
 */

import { apiGet } from "./core.js";

/**
 * How many rounds went how.
 *
 * **Counts, not rates.** *"7 of 9"* and *"78%"* are the same fact, but only one
 * of them survives being added to another one, and only one tells a player the
 * sample was nine rounds rather than ninety. Divide here if a screen wants a
 * percentage; a float on the wire would let two surfaces round it differently.
 */
export interface Tally {
  /**
   * Rounds this player was **scored in** — the denominator for everything
   * below, and not the number of rounds in the game. Classic scores only the
   * active side, so a four-player game gives each of them roughly a quarter.
   */
  rounds: number;
  artist: number;
  title: number;
  slot: number;
  exact_year: number;
  /** Cards won by placing correctly. **Not** cards held at the end. */
  cards_won: number;
  coins: number;
}

/** One slice of a tally — a decade, or a genre family. */
export interface Bucket {
  /** `"1980"`, or a genre slug. */
  key: string;
  /** `"1980s"`, or the genre's name. */
  label: string;
  tally: Tally;
}

/** One player's game. */
export interface PlayerRecap {
  profile_id: string;
  display_name: string;
  won: boolean;
  /** Cards held at the end, **after** any steal — from the recorded standing. */
  cards: number;
  coins: number;
  eliminated: boolean;
  tally: Tally;
  by_decade: Bucket[];
  /** Best effort: a track can lose its tags or leave the catalog. */
  by_genre: Bucket[];
  /** Rounds no genre was found for, so `by_genre` reads honestly. */
  genre_unknown: Tally;
}

/** One round, as it happened. */
export interface RoundLine {
  /** **Not unique** — a void is a re-deal, so two lines can share a number. */
  round_no: number;
  title: string | null;
  artist: string | null;
  year: number | null;
  voided: boolean;
  placed_correctly: string[];
}

/** A finished game. */
export interface Recap {
  game_id: string;
  mode: string;
  rounds_played: number;
  /** Rounds thrown away because the year was wrong (ADR 0009). */
  rounds_voided: number;
  /** Everybody, best first. */
  players: PlayerRecap[];
  timeline: RoundLine[];
}

/** What somebody is good at, across every game. */
export interface Career {
  games: number;
  games_won: number;
  overall: Tally;
  by_decade: Bucket[];
  by_genre: Bucket[];
  genre_unknown: Tally;
  /** `null` until they have played enough for the answer to mean anything. */
  best_decade: string | null;
  worst_decade: string | null;
}

/**
 * One finished game, by id.
 *
 * **No credential.** The game id is the capability — this is the URL people
 * paste into a group chat. A game that is still running answers `404`, on
 * purpose and indistinguishably from one that never existed: telling the two
 * apart would confirm to somebody holding an old link that a party is playing
 * right now.
 */
export async function fetchRecap(gameId: string): Promise<Recap> {
  return apiGet<Recap>(`/recap/${encodeURIComponent(gameId)}`);
}

/**
 * One finished game in the match list — phase 16j-1.
 *
 * Mirrors `MatchLine` in `src/http/recap.rs`. ⚠️ Hand-kept, like the rest of this
 * file: nothing checks it for you.
 */
export interface MatchLine {
  /** The recap's address: `/recap/{game_id}`. */
  game_id: string;
  /** RFC 3339. */
  finished_at: string;
  /** `classic` · `deathmatch` · `team`. */
  mode: string;
  party_name: string | null;
  /** Whether this account's side won it. */
  won: boolean;
}

/** One line on a leaderboard — phase 16j-2. */
export interface BoardLine {
  profile_id: string;
  display_name: string;
  /** **A count, never a rate.** */
  count: number;
}

/**
 * The hall of fame. Mirrors `HallView` in `src/http/recap.rs`.
 *
 * Several small boards rather than one ranking: more people get to be top of
 * something, and it stops "whoever plays most evenings" being permanently first
 * at everything.
 */
export interface HallView {
  most_games_won: BoardLine[];
  most_placements: BoardLine[];
  most_challenges_won: BoardLine[];
  most_steals: BoardLine[];
  /**
   * Whether anything has been played at all.
   *
   * The server's own answer, read rather than re-derived from four empty
   * arrays: "no games yet" reads as **new**, four empty headings read as
   * **broken**.
   */
  empty: boolean;
}

/**
 * The hall of fame.
 *
 * Signed-in only, and it ranks account holders only — guests play without
 * accounts (ADR 0007), so a leaderboard cannot name somebody who never signed up
 * for one.
 */
export async function fetchHall(): Promise<HallView> {
  return apiGet<HallView>("/hall");
}

/** One game in the public archive — phase 16j-3. */
export interface ArchiveLine {
  game_id: string;
  finished_at: string;
  mode: string;
  party_name: string | null;
  /** Everybody who played, guests included. */
  players: string[];
  rounds: number;
}

/**
 * A browsable history of the installation.
 *
 * ⚠️ Signed-in only, and it has to be: an archive names everybody who played or
 * it is a record with holes, and guests play without accounts (ADR 0007).
 */
export async function fetchArchive(): Promise<ArchiveLine[]> {
  return apiGet<ArchiveLine[]>("/archive");
}

/**
 * The signed-in account's finished games, newest first.
 *
 * `Career` is aggregates only, so `/me` could say *you have won seven* and not
 * say *which*. Capped server-side — a career is unbounded and a page is not.
 */
export async function fetchMatches(): Promise<MatchLine[]> {
  return apiGet<MatchLine[]>("/me/matches");
}

/**
 * The signed-in account's own history.
 *
 * Needs a session, and a guest has none — playing needs no account, and
 * continuity is what an account is *for* (ADR 0007). Claiming a guest profile
 * later brings every game with it.
 */
export async function fetchProgression(): Promise<Career> {
  return apiGet<Career>("/me/progression");
}
